/**
 * Calculate the relative luminance of a color
 * Based on WCAG 2.0 guidelines
 */
function getLuminance(hex: string): number {
  // Remove # if present
  const color = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(color.substring(0, 2), 16) / 255;
  const g = parseInt(color.substring(2, 4), 16) / 255;
  const b = parseInt(color.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const [rLinear, gLinear, bLinear] = [r, g, b].map((val) => {
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Calculate contrast ratio between two colors
 * Returns a value between 1 and 21
 */
export function getContrastRatio(color1: string, color2: string): number {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determine if a color is light or dark
 * Returns true if light (should use black text), false if dark (should use white text)
 */
export function isLightColor(hex: string): boolean {
  const luminance = getLuminance(hex);
  // Threshold of 0.5 is standard - above is light, below is dark
  return luminance > 0.5;
}

/**
 * Get appropriate text color (black or white) based on background color
 * Uses WCAG 2.0 contrast ratio guidelines
 */
export function getTextColorForBackground(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  // Use 0.5 threshold - above is light (use black), below is dark (use white)
  // This ensures WCAG AA compliance (4.5:1 contrast ratio)
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/**
 * Get appropriate secondary text color (muted) based on background color
 * Ensures sufficient contrast for readability
 */
export function getSecondaryTextColorForBackground(backgroundColor: string): string {
  const isLight = isLightColor(backgroundColor);
  // Use higher opacity for better contrast
  return isLight ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)';
}

/**
 * Get text color with custom opacity
 */
export function getTextColorWithOpacity(backgroundColor: string, opacity: number = 0.7): string {
  const isLight = isLightColor(backgroundColor);
  return isLight 
    ? `rgba(0, 0, 0, ${opacity})` 
    : `rgba(255, 255, 255, ${opacity})`;
}

/**
 * Ensure contrast ratio meets WCAG AA standards (4.5:1 for normal text)
 * Returns adjusted color if needed
 */
export function ensureContrastRatio(
  foregroundColor: string, 
  backgroundColor: string, 
  minRatio: number = 4.5
): string {
  const ratio = getContrastRatio(foregroundColor, backgroundColor);
  
  if (ratio >= minRatio) {
    return foregroundColor;
  }

  // If contrast is too low, return the opposite color
  return getTextColorForBackground(backgroundColor);
}

